import { isEqual, isNull, uniq } from 'lodash/fp';

import { ApplicationState } from '../../../../../store/types';
import { MLData } from '../../../../datasets/store/mlTypes';
import { AnalysisView } from '../../../../documents/store/types';
import { ColumnSort, TableFilters } from '../../../../shared/table/types';
import { getCurrentDocumentQueries, getSavedDocumentQueries } from '../../queries/store/selectors';
import { getAIPlaygroundRoot } from '../../store/selectors';
import { AIPlaygroundTab, AllPreviousPlaygroundQuestionsAndAnswers, FileWithDocumentNameId, MLTestDocument, MLTestDocumentTable, PlaygroundQuestion, PreviousQuestionsTab, QuestionsAndAnswers } from './types';
import { DocumentQuery } from '../../queries/store/types';

export const getIsFetchingMLTestDocuments = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).isFetchingMLTestDocuments;

export const getMLTestDocuments = (state: ApplicationState): MLTestDocumentTable[] => getAIPlaygroundRoot(state).mlTestDocuments;

export const getMLTestDocumentsUploadModalOpen = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).mlTestDocumentsUploadModalOpen;

export const getMLTestFilesToUpload = (state: ApplicationState): File[] | null => getAIPlaygroundRoot(state).filesToUpload;

export const getIsUploadingMLTestDocument = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).isUploadingMLTestDocument;

export const getFileDocumentNameIds = (state: ApplicationState): FileWithDocumentNameId[] => getAIPlaygroundRoot(state).fileDocumentNameIds;

export const getMLDocumentsTableFilters = (state: ApplicationState): TableFilters => getAIPlaygroundRoot(state).mlDocumentsTableFilters;
export const getMLDocumentsTablePageNumber = (state: ApplicationState): number => getAIPlaygroundRoot(state).mlDocumentsTablePageNumber;
export const getMLDocumentsTableColumnSort = (state: ApplicationState): ColumnSort | undefined => getAIPlaygroundRoot(state).mlDocumentsTableColumnSort;
export const getMLDocumentsTablePageSize = (state: ApplicationState): number => getAIPlaygroundRoot(state).mlDocumentsTablePageSize;
export const getMLTestDocumentsTotal = (state: ApplicationState): number => getAIPlaygroundRoot(state).mlTestDocumentsTotal;

export const getMLTestDocument = (state: ApplicationState): MLTestDocument | null => getAIPlaygroundRoot(state).mlTestDocument;

export const getMLTestDocumentUrl = (state: ApplicationState): string | null => getAIPlaygroundRoot(state).mlTestDocumentUrl;

export const getIsOpening = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).isOpening;

export const getMLTestDocumentAnalysisView = (state: ApplicationState): AnalysisView => getAIPlaygroundRoot(state).mlTestDocumentAnalysisView;

export const getAIPlaygroundSearchTerm = (state: ApplicationState): string => getAIPlaygroundRoot(state).aiPlaygroundSearchTerm;

export const getSelectedPage = (state: ApplicationState): number | null => getAIPlaygroundRoot(state).selectedPage;

export const getDocumentNameIdQueries = (state: ApplicationState): DocumentQuery[] => {
    const mlTestDocument = getMLTestDocument(state);
    const currentQueries = getCurrentDocumentQueries(state);
    return !isNull(mlTestDocument) ? currentQueries.filter(({ documentNameIds }) => documentNameIds.includes(mlTestDocument.documentNameId!)) : [];
};

export const getFilteredQueries = (state: ApplicationState): DocumentQuery[] => {
    const filteredQueries = getDocumentNameIdQueries(state);
    const aiPlaygroundSearchTerm = getAIPlaygroundSearchTerm(state);
    return filteredQueries.filter(({ question }) => question.toLowerCase().includes(aiPlaygroundSearchTerm.toLowerCase())).sort((a, b) => a.queryOrder - b.queryOrder);
};

export const getCurrentSelectedQueries = (state: ApplicationState): PlaygroundQuestion[] => getAIPlaygroundRoot(state).currentSelectedQueries;

export const getCurrentCorrectQueries = (state: ApplicationState): number[] => getAIPlaygroundRoot(state).currentCorrectQueries;

export const getMlTestDocumentExtractId = (state: ApplicationState): number | null => getAIPlaygroundRoot(state).mlTestDocumentExtractId;

export const getSavedSelectedQueries = (state: ApplicationState): PlaygroundQuestion[] => getAIPlaygroundRoot(state).selectedQueries;

export const getConfirmationModalVisible = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).confirmationModalVisible;

export const getSelectedQueriesUpdated = (state: ApplicationState): boolean => {
    const currentQueries = getCurrentSelectedQueries(state);
    const savedQueries = getSavedSelectedQueries(state);
    return isEqual(currentQueries, savedQueries);
};

export const getAIResults = (state: ApplicationState): MLData | null => getAIPlaygroundRoot(state).aiResults;

export const getIsAskingAIPlaygroundQuestions = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).isAskingAIPlaygroundQuestions;

export const getPlaygroundQuestionsConfirmDisabled = (state: ApplicationState): string[] => {
    const selectedQueries = getCurrentSelectedQueries(state);
    const { scheduleStartPage, scheduleEndPage } = getMLTestDocument(state)!;
    const allSelectedHaveQuestionLength = selectedQueries.map(({ question }) => question).every(question => question.length > 0);
    const allQueryKeys = selectedQueries.map(({ queryKey }) => queryKey);
    const allSelectedHaveQueryKeyLength = allQueryKeys.every(queryKey => queryKey.length > 0);
    const allKeysAreUnique = uniq(allQueryKeys).length === allQueryKeys.length;
    let tooltip: string[] = [];
    if (!selectedQueries.length) {
        tooltip.push('You have not selected any questions');
    }
    if (!allSelectedHaveQuestionLength) {
        tooltip.push('One or more of the selected queries is missing a question');
    }
    if (!allSelectedHaveQueryKeyLength) {
        tooltip.push('One or more of the selected queries is missing a query key');
    }
    if (!allKeysAreUnique) {
        tooltip.push('All query keys must be unique');
    }
    if (isNull(scheduleStartPage) || scheduleEndPage) {
        tooltip.push('You must provide a schedule start and a schedule end page for your document');
    }
    return tooltip;
};

export const getIsFilteringSelectedQueries = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).isFilteringQuerySelection;

export const getSelectedAIPlaygroundTab = (state: ApplicationState): AIPlaygroundTab => getAIPlaygroundRoot(state).selectedAIPlaygroundTab;

export const getProposedAIPlaygroundTab = (state: ApplicationState): AIPlaygroundTab | null => getAIPlaygroundRoot(state).proposedAIPlaygroundTab;

export const getIsFetchingAllPreviousQuestionsAndAnswers = (state: ApplicationState): boolean => getAIPlaygroundRoot(state).isFetchingAllPreviousQuestionsAndAnswers;

export const getAllPreviousQuestionsAndAnswers = (state: ApplicationState): AllPreviousPlaygroundQuestionsAndAnswers | null => getAIPlaygroundRoot(state).allPreviousQuestionsAndAnswers;

export const getCustomQueries = (state: ApplicationState): QuestionsAndAnswers[] => {
    const savedQueries = getSavedDocumentQueries(state);
    const previousQuestionsAndAnswers = getAllPreviousQuestionsAndAnswers(state);
    const previouslyAskedQuestions = !isNull(previousQuestionsAndAnswers) ? previousQuestionsAndAnswers.previouslyAskedQuestions : [];
    let filteredQuestionsAndAnswers: QuestionsAndAnswers[] = [];
    previouslyAskedQuestions.forEach(({ questionsAndAnswers }) => questionsAndAnswers.map(instance => filteredQuestionsAndAnswers.push(instance)));
    const uniqueQuestionsAndAnswers = Array.from(new Map(filteredQuestionsAndAnswers.map(object => [object.queryKey, object])).values());
    const customQueries = uniqueQuestionsAndAnswers.filter(({ queryKey }) => !savedQueries.map(({ queryKey }) => queryKey).includes(queryKey));
    return customQueries;
};

export const getPreviouslyAskedQueriesSearchTerm = (state: ApplicationState): string => getAIPlaygroundRoot(state).previouslyAskedQueriesSearchTerm;

export const getPreviouslyAskedFilteredQueries = (state: ApplicationState): QuestionsAndAnswers[] => {
    const filteredQueries = getCustomQueries(state);
    const previousQuestionSearchTerm = getPreviouslyAskedQueriesSearchTerm(state);
    return filteredQueries.filter(({ question }) => question.toLowerCase().includes(previousQuestionSearchTerm.toLowerCase()));
};

export const getSelectedPreviousQuestionsTab = (state: ApplicationState): PreviousQuestionsTab => getAIPlaygroundRoot(state).selectedPreviousQuestionsTab;
